import React, { useContext, useMemo } from 'react';
import { ReactComponent as PlatesIcon } from 'Icons/ExperimentTab/ExperimentPlate.svg';
import { ReactComponent as DispensedPlateIcon } from 'Icons/ExperimentTab/ExperimentPlateDispensed.svg';
import { PlateInfoDTO } from 'business/models/Models';
import ProjectSidePanelContext from './projectSidePanelContext';
import { useFetchPlateNodeCustomNameProperty } from './hooks';
import EditBarcode from './EditBarcode';
import useRMLite from 'CustomHooks/useRMLite';
import { EPlateState } from 'enums/EPlatestate';
import './ExperimentPlateInfoPanel.scss';

export interface ExperimentPlateInfoPanelProps {
  expDTO: PlateInfoDTO[];
  experimentId: number;
  plates: any[];
  isEditBarcodeAllowed: boolean;
}

let plateInfoKeys = ['barcode', 'id', 'plateNumber'];
const returnData = (plate, plateNodeNameFormatProperty) => {
  let res = '';
  if (plateNodeNameFormatProperty) {
    plateInfoKeys = plateNodeNameFormatProperty.value
      .split(',')
      .map((item) =>
        item
          .replace(/[<>]/g, '')
          .trim()
          .replace('plateNum', 'plateNumber')
          .replace('plateID', 'id'),
      )
      .filter((item) => item !== '');
  }

  for (const key of plateInfoKeys) {
    if (plate[key]) {
      res += plate[key] + ', ';
    }
  }
  res = res.substring(0, res.length - 2);
  return res;
};
const plateClickHandler = (experimentId, selectedPlate) => () => {
  if (experimentId) {
    const link = `/exp/${experimentId}/plate/${selectedPlate.id}`;
    window.open(link, link);
  }
};

const selectPlate = (plate, setSelectedPlateList) => {
  setSelectedPlateList((prev) => {
    let selection = [...prev];

    if (selection.some((p) => p.id === plate.id)) {
      selection = selection.filter((p) => p.id !== plate.id);
    } else {
      selection = [plate];
    }

    return selection;
  });
};

const getExpInfo = (
  expDTO: PlateInfoDTO[],
  experimentId,
  selectedPlateList,
  setSelectedPlateList,
  plateNodeNameFormatProperty,
) => {
  const content = [];
  expDTO.map((plate, index) => {
    const plateInfo = returnData(plate, plateNodeNameFormatProperty);
    const isDispensed = plate.dispensed.toLocaleLowerCase().trim() === 'true';
    content.push(
      <li
        id={`experimentplateinfopanel-expinfo-${plateInfo.replace(/\s+/g, '-')}-${index}`}
        className="list-item-container exp-plate-info-item node"
        title={plateInfo}
      >
        <div
          id={`experimentplateinfopanel-expinfo-content-${plate.id}`}
          className={`list-content flex-xy-center flex-column
          ${experimentId ? 'cursor-pointer' : ''}
          ${
            selectedPlateList.some((plt) => plt.id === plate.id)
              ? 'selected-explorer-plate'
              : 'transparent-background'
          }`}
          onClick={() => selectPlate(plate, setSelectedPlateList)}
          onDoubleClick={plateClickHandler(experimentId, plate)}
        >
          {isDispensed ? (
            <DispensedPlateIcon
              id={`experimentplateinfopanel-expinfo-dispensed-${plate.id}`}
              className="mt-2"
            />
          ) : (
            <PlatesIcon
              id={`experimentplateinfopanel-expinfo-regular-${plate.id}`}
              className="mt-2"
            />
          )}
          <p
            id={`experimentplateinfopanel-expinfo-plate-text-${plate.id}`}
            className="noSelect-plate nodeText"
          >
            {plateInfo}
          </p>
        </div>
      </li>,
    );
  });
  return content;
};

const ExperimentPlateInfoPanel = (props: ExperimentPlateInfoPanelProps) => {
  const { expDTO, experimentId, isEditBarcodeAllowed } = props;
  const projectSidePanelContext = useContext(ProjectSidePanelContext);
  const plateNodeNameFormatProperty = useFetchPlateNodeCustomNameProperty({
    experimentId,
  });
  const rmLite = useRMLite();

  const getContent = useMemo(() => {
    return getExpInfo(
      expDTO,
      experimentId,
      projectSidePanelContext.selectedPlateList,
      projectSidePanelContext.setSelectedPlateList,
      plateNodeNameFormatProperty,
    );
  }, [
    expDTO,
    experimentId,
    projectSidePanelContext.selectedPlateList,
    plateNodeNameFormatProperty,
  ]);

  const selectedPlate = useMemo(() => {
    if (projectSidePanelContext.selectedPlateList.length !== 1) {
      return null;
    }

    const selectedId = projectSidePanelContext.selectedPlateList[0].id;

    return expDTO.find((p) => p.id === selectedId) || null;
  }, [expDTO, projectSidePanelContext.selectedPlateList]);

  return (
    <div id="experimentplateinfopanel-wrapper" className="py-2 px-3">
      <div
        id="experimentplateinfopanel-header"
        className="nodeInfo nodeLabel flex flex-row w-full p-[1] justify-between items-center"
      >
        <div id="experimentplateinfopanel-title" className="flex flex-row">
          <div id="experimentplateinfopanel-label" className="mr-1">
            Plate Preview
          </div>
          (
          <div id="experimentplateinfopanel-count" className=" text-[#006af5]">
            {expDTO.length}
          </div>
          )
        </div>
        {!rmLite && (
          <div id="experimentplateinfopanel-editbarcode">
            <EditBarcode
              plate={selectedPlate}
              setSelectedPlateList={projectSidePanelContext.setSelectedPlateList}
              onEditPlateBarcode={projectSidePanelContext.onEditPlateBarcode}
              disabled={
                !isEditBarcodeAllowed ||
                rmLite ||
                !selectedPlate ||
                selectedPlate.plateState >= EPlateState.Dispensed
              }
            />
          </div>
        )}
      </div>
      {getContent.length > 0 && (
        <ul id="experimentplateinfopanel-content" className="exp-plate-info-container">
          {getContent}
        </ul>
      )}
    </div>
  );
};

export default ExperimentPlateInfoPanel;
